import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { GLOBAL_ASSISTANT_FILES_SOURCE } from '@/features/assistant-files/assistant-files-query';
import { AssistantFilesViewer } from '@/features/assistant-files/AssistantFilesViewer';
import { AssistantNotesEditor } from '@/features/assistant-notes';
import {
  CustomInstructionsTextArea,
  useChatPromptFields,
} from '@/features/settings/sub-settings/chat-prompt-fields';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';

import { ECHONOTES_SECTION_DEFS } from './echonotes-query';
import { EchoNotesViewer } from './EchoNotesViewer';

export const MemoryTabsPage = observer(function MemoryTabsPage() {
  const echoNotesEnabled = userStore.isInternalUser;
  const fields = useChatPromptFields();
  const [selectedTabs, setSelectedTabs] = useState<string[]>(['memory']);
  const [compareMode, setCompareMode] = useState(false);

  const availableTabs = useMemo(() => {
    const tabs: { value: string; label: string }[] = [
      { value: 'custom-instructions', label: 'Custom Instructions' },
      { value: 'memory', label: 'Assistant Notes' },
    ];
    if (echoNotesEnabled) {
      for (const { tabValue, label } of ECHONOTES_SECTION_DEFS) {
        tabs.push({ value: tabValue, label });
      }
    }
    return tabs;
  }, [echoNotesEnabled]);

  function handleTabClick(value: string) {
    if (!compareMode) {
      setSelectedTabs([value]);
      return;
    }
    setSelectedTabs((prev) => {
      if (prev.includes(value)) {
        return prev.length > 1 ? prev.filter((v) => v !== value) : prev;
      }
      if (prev.length < 2) {
        return [...prev, value];
      }
      return [prev[1], value];
    });
  }

  function toggleCompareMode() {
    setCompareMode((prev) => {
      if (prev) {
        // Switching to single: keep only the last selected tab
        setSelectedTabs((tabs) => (tabs.length > 1 ? [tabs[1]] : tabs));
      }
      return !prev;
    });
  }

  function renderContent(tabValue: string) {
    if (tabValue === 'custom-instructions') {
      return (
        <YStack className="h-full px-4 py-2">
          <YStack className="max-w-screen-md mx-auto w-full h-full gap-2">
            <CustomInstructionsTextArea
              wrapperClassName="flex-1 min-h-0"
              className="flex-1 min-h-0 text-sm resize-none"
              fields={fields}
            />
          </YStack>
        </YStack>
      );
    }
    if (tabValue === 'memory') {
      return (
        <YStack className="h-full px-4 py-2">
          <YStack className="max-w-screen-md mx-auto w-full h-full gap-4">
            <AssistantNotesEditor
              wrapperClassName="flex-1 min-h-0"
              className="flex-1 min-h-0 text-sm resize-none"
            />
            <YStack className="gap-2 min-h-0 flex-1">
              <span className="text-sm font-medium text-foreground">
                VFS extension of Assistant Notes
              </span>
              <AssistantFilesViewer
                className="flex-1 min-h-0"
                panelHeightClassName="h-full min-h-[16rem]"
                source={GLOBAL_ASSISTANT_FILES_SOURCE}
              />
            </YStack>
          </YStack>
        </YStack>
      );
    }
    const def = ECHONOTES_SECTION_DEFS.find((d) => d.tabValue === tabValue);
    if (!def) return null;
    return (
      <YStack className="h-full min-w-0 overflow-y-auto overflow-x-hidden px-4 py-2">
        <YStack className="max-w-screen-md mx-auto w-full min-w-0">
          <EchoNotesViewer section={def.section} maxLength={def.maxLength} />
        </YStack>
      </YStack>
    );
  }

  // Sort selections by their position in the tab bar
  const orderedSelections = useMemo(
    () => availableTabs.filter((tab) => selectedTabs.includes(tab.value)),
    [availableTabs, selectedTabs]
  );

  const isSplit = orderedSelections.length === 2;

  return (
    <YStack className="h-full select-text gap-2">
      <XStack className="px-4 items-center gap-2">
        <div
          className={cn(
            'bg-muted text-muted-foreground inline-flex gap-1',
            'w-fit items-center justify-center rounded-lg p-[3px]',
            'border border-border-strong h-9'
          )}
        >
          {availableTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => handleTabClick(tab.value)}
              className={cn(
                'text-xs inline-flex items-center justify-center',
                'h-[calc(100%-1px)] rounded-md border px-2 py-1',
                'whitespace-nowrap transition-colors',
                'hover:bg-secondary-hover focus-ring',
                selectedTabs.includes(tab.value)
                  ? 'bg-secondary-hover font-medium border-border dark:border-input'
                  : 'border-transparent'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={toggleCompareMode}
          className={cn(
            'inline-flex items-center text-xs h-9 rounded-lg border px-3 py-1',
            'whitespace-nowrap transition-colors focus-ring',
            compareMode
              ? 'bg-secondary-hover border-border dark:border-input'
              : 'bg-muted text-muted-foreground border-border-strong hover:bg-secondary-hover'
          )}
        >
          {compareMode && (
            <Icons.check className="mr-1.5 h-3.5 w-3.5 shrink-0" />
          )}
          Compare sections
        </button>
      </XStack>

      <XStack className="flex-1 overflow-hidden">
        {orderedSelections.map((tab, index) => (
          <YStack
            key={tab.value}
            className={cn(
              'h-full min-w-0 overflow-hidden',
              isSplit ? 'w-1/2' : 'w-full',
              isSplit && index === 0 && 'border-r border-border'
            )}
          >
            {isSplit && (
              <h3 className="px-4 pt-2 text-sm font-medium text-foreground-muted">
                {tab.label}
              </h3>
            )}
            {renderContent(tab.value)}
          </YStack>
        ))}
      </XStack>
    </YStack>
  );
});
